import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Rocket, Plus, Trash2, RotateCcw, GitCommit, Info, Search, UserPlus, Users, Sliders, LayoutDashboard, Zap, Check, FolderTree, Folder, FileText, Lock, ChevronRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fmtDate = (s) => (s && !isNaN(Date.parse(s)) ? new Date(s).toLocaleString() : "—");
const STATUS_C = { draft: "text-[#A1A1AA]", ready: "text-[#00F0FF]", live: "text-[#22c55e]", rolled_back: "text-[#f59e0b]" };
const ROLLOUTS = [["off", "OFF"], ["admin_only", "Admin only"], ["internal_test", "Internal test"], ["all", "All users"]];
const ACCESS_OPTIONS = [["off", "OFF"], ["admin_only", "Admin only"], ["internal_testers", "Internal testers"], ["all_users", "All users"], ["specific_users", "Specific users"]];
const ACCESS_LABEL = Object.fromEntries(ACCESS_OPTIONS);
const PLAN_OPTIONS = [["free", "Free"], ["pro", "Pro"]];
const GROUP_FOR_ACCESS = { admin_only: "admin", internal_testers: "internal_tester", specific_users: "specific_user" };
const GROUP_LABEL = { admin: "Admin access", internal_tester: "Internal testers", specific_user: "Specific users" };

function ConfirmDialog({ open, word, title, body, onConfirm, onClose, busy }) {
  const [txt, setTxt] = useState("");
  useEffect(() => { if (open) setTxt(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-[#111] text-white">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">{body} Type <span className="font-mono font-bold text-white">{word}</span> to confirm.</DialogDescription>
        <input autoFocus data-testid="rel-confirm-input" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={word}
          className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
          <button data-testid="rel-confirm-btn" disabled={txt !== word || busy} onClick={() => onConfirm(txt)}
            className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />} {word}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReleasesTab() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ version: "", name: "", notes: "" });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.adminReleases().then(setData).catch(() => setData({ releases: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.version.trim()) { toast.error("Version required"); return; }
    setBusy(true);
    try { await api.adminReleaseCreate(form); toast.success("Release created"); setModal(false); setForm({ version: "", name: "", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const runConfirm = async (c) => { setBusy(true); try { await confirm.run(c); setConfirm(null); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } setBusy(false); };
  const publish = (r) => setConfirm({ word: "DEPLOY", title: `Mark ${r.version} Live`, body: "Activates this release marker and runs a health check. This does NOT deploy code.", run: (c) => api.adminReleasePublish(r.id, c).then(() => toast.success("Release is now Live")) });
  const rollback = (r) => setConfirm({ word: "ROLLBACK", title: `Roll back to ${r.version}`, body: "Makes this the live release marker.", run: (c) => api.adminReleaseRollback(r.id, c).then(() => toast.success("Rolled back")) });
  const setReady = async (r) => { try { await api.adminReleaseUpdate(r.id, { status: "ready" }); load(); } catch { toast.error("Failed"); } };
  const del = async (r) => { if (!window.confirm(`Delete ${r.version}?`)) return; try { await api.adminReleaseDelete(r.id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const cur = data.current;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/5 p-3 text-xs text-[#f5d08a]" data-testid="rel-notice">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>{data.notice} Emergent does not expose a deployment API, so marking a release <b>Live</b> is an activation/tracking marker — not a code deploy.</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="rel-banner">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA]">Production</div>
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-black text-white">{cur ? cur.version : "No live release"}</span>
            {cur && <span className="flex items-center gap-1 text-sm font-semibold text-[#22c55e]">● LIVE</span>}
          </div>
          <div className="mt-1 flex items-center gap-1 text-[11px] text-[#A1A1AA]"><GitCommit size={12} /> build {data.current_sha || "unknown"}{cur?.live_at ? ` · live ${fmtDate(cur.live_at)}` : ""}</div>
        </div>
        <button onClick={() => setModal(true)} data-testid="rel-create-btn" className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black"><Plus size={15} /> Create Release</button>
      </div>

      <div className="space-y-2" data-testid="rel-list">
        {data.releases.length === 0 ? <div className="rounded-2xl border border-white/10 py-12 text-center text-[#A1A1AA]">No releases yet.</div> :
          data.releases.map((r) => (
            <div key={r.id} data-testid={`rel-row-${r.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base font-bold text-white">{r.version}</span>
                    {r.name && <span className="text-sm text-white/70">— {r.name}</span>}
                    <span className={`text-xs font-bold uppercase ${STATUS_C[r.status] || "text-[#A1A1AA]"}`}>{r.status.replace("_", " ")}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#A1A1AA]"><GitCommit size={11} className="mr-1 inline" />{r.commit_sha || "—"} · created {fmtDate(r.created_at)} by {r.created_by}</div>
                  {r.notes && <pre className="mt-2 whitespace-pre-wrap font-plex text-xs text-white/60">{r.notes}</pre>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {r.status === "draft" && <button data-testid={`rel-ready-${r.id}`} onClick={() => setReady(r)} className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#00F0FF] hover:bg-white/5">Mark Ready</button>}
                  {r.status !== "live" && <button data-testid={`rel-publish-${r.id}`} onClick={() => publish(r)} className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-2.5 py-1.5 text-xs font-bold text-black"><Rocket size={12} /> Mark Live</button>}
                  {r.status === "rolled_back" && <button data-testid={`rel-rollback-${r.id}`} onClick={() => rollback(r)} className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#f59e0b] hover:bg-white/5"><RotateCcw size={12} /> Rollback</button>}
                  {r.status !== "live" && <button data-testid={`rel-delete-${r.id}`} onClick={() => del(r)} className="rounded-lg border border-white/15 p-1.5 text-[#ef4444] hover:bg-white/5"><Trash2 size={13} /></button>}
                </div>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={modal} onOpenChange={(o) => !o && setModal(false)}>
        <DialogContent className="max-w-lg border-white/10 bg-[#111] text-white">
          <DialogTitle>Create Release</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Records a version + notes (git build {data.current_sha || "unknown"} captured automatically).</DialogDescription>
          <div className="mt-2 space-y-3">
            <input data-testid="rel-version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="Version e.g. v2.5.0" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
            <input data-testid="rel-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Release name e.g. Timeline Improvements" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
            <textarea data-testid="rel-notes" rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Release notes…" className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setModal(false)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
            <button data-testid="rel-save" disabled={busy} onClick={create} className="flex items-center gap-2 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirm} word={confirm?.word} title={confirm?.title} body={confirm?.body} busy={busy} onClose={() => setConfirm(null)} onConfirm={runConfirm} />
    </div>
  );
}

function GrantDialog({ feature, onClose, onChanged }) {
  const group = GROUP_FOR_ACCESS[feature.access_mode];
  const [grants, setGrants] = useState(null);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("free");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.adminFeatureGrants(feature.feature_key).then((d) => setGrants(d.grants || {})).catch(() => setGrants({}));
  }, [feature.feature_key]);
  useEffect(() => { load(); }, [load]);
  const rows = (grants && group) ? (grants[group] || []) : [];

  const add = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!plan) { toast.error("Select Free or Pro"); return; }
    setBusy(true);
    try { await api.adminFeatureGrantAdd(feature.feature_key, { group, email: email.trim(), plan }); toast.success("Access granted"); setEmail(""); setPlan("free"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const remove = async (em) => {
    try { await api.adminFeatureGrantRemove(feature.feature_key, group, em); load(); onChanged && onChanged(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="grant-dialog" className="max-w-lg border-white/10 bg-[#0b0b0b] text-white">
        <DialogTitle>{feature.feature_name} — Access</DialogTitle>
        <DialogDescription className="text-[#A1A1AA]">Access: <b className="text-white">{ACCESS_LABEL[feature.access_mode]}</b> · Plan: <b className="text-white capitalize">{feature.plan}</b></DialogDescription>
        {!group ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm text-[#A1A1AA]">
            {feature.access_mode === "all_users" ? "This feature is available to all users — no per-user list is needed." :
             feature.access_mode === "off" ? "This feature is OFF for everyone (including admins). Change Access to add users." :
             "No user list applies for this access mode."}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#666]">Add to {GROUP_LABEL[group]}</div>
              <input data-testid="grant-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@email.com"
                className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
              <div className="mt-2 flex items-center gap-3">
                <span className="text-xs text-[#A1A1AA]">Access plan:</span>
                {PLAN_OPTIONS.map(([v, l]) => (
                  <label key={v} className="flex cursor-pointer items-center gap-1.5 text-sm text-white">
                    <input type="radio" name="grant-plan" data-testid={`grant-plan-${v}`} checked={plan === v} onChange={() => setPlan(v)} /> {l}
                  </label>
                ))}
                <button data-testid="grant-submit" disabled={busy} onClick={add}
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-sm font-bold text-black disabled:opacity-50"><UserPlus size={14} /> Grant Access</button>
              </div>
              <div className="mt-1.5 text-[10px] text-[#666]">Free/Pro must be chosen for every grant. Pro grants comp the feature; Free grants rely on the user's own Pro entitlement when the feature Plan is Pro.</div>
            </div>
            {rows === null ? <Loader2 className="mx-auto animate-spin text-[#00F0FF]" /> :
              rows.length === 0 ? <div className="rounded-xl border border-white/10 py-6 text-center text-sm text-[#A1A1AA]" data-testid="grant-empty">No {GROUP_LABEL[group].toLowerCase()} yet.</div> : (
                <div className="max-h-64 overflow-y-auto rounded-xl border border-white/10">
                  {rows.map((g) => (
                    <div key={g.email} data-testid={`grant-row-${g.email}`} className="flex items-center justify-between border-b border-white/5 px-3 py-2 text-sm last:border-0">
                      <div>
                        <span className="text-white/90">{g.email}</span>
                        <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${g.plan === "pro" ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-white/10 text-[#A1A1AA]"}`}>{(g.plan || "free").toUpperCase()}</span>
                        {!g.user_exists && <span className="ml-1 text-[10px] text-[#f59e0b]">no account yet</span>}
                        <div className="text-[11px] text-[#666]">by {g.granted_by || "—"} · {fmtDate(g.created_at)}</div>
                      </div>
                      <button data-testid={`grant-remove-${g.email}`} onClick={() => remove(g.email)} className="rounded-lg border border-white/15 p-1.5 text-[#ef4444] hover:bg-white/5"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
        <div className="mt-2 flex justify-end"><button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Done</button></div>
      </DialogContent>
    </Dialog>
  );
}

function RolloutTab() {
  const [feats, setFeats] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [planConfirm, setPlanConfirm] = useState(null);
  const load = useCallback(() => { api.adminFeaturesList ? null : null; import("@/lib/api").then(({ http }) => http.get("/admin/features").then((r) => setFeats(r.data)).catch(() => setFeats([]))); }, []);
  useEffect(() => { load(); }, [load]);

  const update = async (key, body) => {
    try {
      const d = await api.adminFeatureUpdate(key, body);
      setFeats((fs) => fs.map((f) => f.feature_key === key ? { ...f, ...d } : f));
      toast.success("Feature updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setAccess = (f, access_mode) => update(f.feature_key, { access_mode });
  const setPlan = (f, plan) => {
    if (plan === f.plan) return;
    setPlanConfirm({ f, plan });
  };
  const confirmPlan = async () => {
    const { f, plan } = planConfirm;
    setPlanConfirm(null);
    await update(f.feature_key, { plan });
  };

  if (!feats) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const rows = feats.filter((f) => !q || (f.feature_name || "").toLowerCase().includes(q.toLowerCase()) || (f.feature_key || "").includes(q.toLowerCase()));
  const eligible = (f) => {
    if (f.access_mode === "all_users") return "All";
    if (f.access_mode === "off") return "0";
    const g = f.grant_counts || {};
    const key = GROUP_FOR_ACCESS[f.access_mode];
    const n = key ? (g[key] || 0) : 0;
    return f.access_mode === "admin_only" ? `Admins + ${n}` : `${n}`;
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-[#A1A1AA]">Every feature has an <b>Access</b> audience and a <b>Plan</b> (Free/Pro), enforced server-side. <b>OFF</b> = nobody (incl. admin). Pro features redirect non-Pro users to pricing. Use <b>Edit</b> to manage per-feature admin / internal-tester / specific-user grants (each grant picks Free or Pro).</div>
      <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" /><input data-testid="rollout-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search features…" className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" /></div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wide text-[#A1A1AA]">
            <tr><th className="px-3 py-2.5 text-left">Feature</th><th className="px-3 py-2.5 text-left">Access</th><th className="px-3 py-2.5 text-left">Plan</th><th className="px-3 py-2.5 text-left">Status</th><th className="px-3 py-2.5 text-left">Eligible</th><th className="px-3 py-2.5 text-left">Updated</th><th className="px-3 py-2.5"></th></tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const on = f.access_mode !== "off";
              return (
                <tr key={f.feature_key} className="border-b border-white/5" data-testid={`rollout-row-${f.feature_key}`}>
                  <td className="px-3 py-2.5"><div className="font-semibold text-white/90">{f.feature_name}{f.critical && <span className="ml-1 text-[10px] text-[#f59e0b]">critical</span>}</div><div className="text-[11px] text-[#A1A1AA]">{f.feature_key}</div></td>
                  <td className="px-3 py-2.5">
                    <select data-testid={`access-select-${f.feature_key}`} value={f.access_mode} onChange={(e) => setAccess(f, e.target.value)} className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
                      {ACCESS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <select data-testid={`plan-select-${f.feature_key}`} value={f.plan} onChange={(e) => setPlan(f, e.target.value)} className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
                      {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5"><span data-testid={`rollout-status-${f.feature_key}`} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${on ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{on ? "ON" : "OFF"}</span></td>
                  <td className="px-3 py-2.5 text-[#A1A1AA]" data-testid={`rollout-eligible-${f.feature_key}`}>{eligible(f)}</td>
                  <td className="px-3 py-2.5 text-[11px] text-[#666]">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2.5">
                    <button data-testid={`rollout-edit-${f.feature_key}`} onClick={() => setEditing(f)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <GrantDialog feature={feats.find((x) => x.feature_key === editing.feature_key) || editing} onClose={() => setEditing(null)} onChanged={load} />}
      <Dialog open={!!planConfirm} onOpenChange={(o) => !o && setPlanConfirm(null)}>
        <DialogContent data-testid="plan-confirm-dialog" className="max-w-md border-white/10 bg-[#0b0b0b] text-white">
          <DialogTitle>Change plan to {planConfirm?.plan?.toUpperCase()}?</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">{planConfirm?.f?.feature_name}: <b className="text-white">{planConfirm?.f?.plan?.toUpperCase()}</b> → <b className="text-white">{planConfirm?.plan?.toUpperCase()}</b>. {planConfirm?.plan === "pro" ? "Non-Pro users will be redirected to pricing." : "Users will no longer need Pro."}</DialogDescription>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setPlanConfirm(null)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
            <button data-testid="plan-confirm-btn" onClick={confirmPlan} className="rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90">Confirm</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestersTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const load = useCallback(() => { api.adminTestUsers(q).then((d) => setRows(d.users || [])).catch(() => setRows([])); }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const add = async () => { if (!email.includes("@")) { toast.error("Enter a valid email"); return; } try { await api.adminTestUserAdd(email); toast.success("Added to internal testers"); setEmail(""); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const remove = async (e) => { try { await api.adminTestUserRemove(e); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-[#A1A1AA]">Users here can see features set to <b>Internal test</b> (plus all admins). Managed by email — nothing hard-coded.</div>
      <div className="flex gap-2">
        <input data-testid="tester-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tester@email.com" className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
        <button data-testid="tester-add" onClick={add} className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black"><UserPlus size={15} /> Add</button>
      </div>
      <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" /><input data-testid="tester-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search testers…" className="w-full rounded-lg border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-[#00F0FF]/50" /></div>
      {rows === null ? <Loader2 className="mx-auto animate-spin text-[#00F0FF]" /> :
        rows.length === 0 ? <div className="rounded-2xl border border-white/10 py-10 text-center text-[#A1A1AA]" data-testid="tester-empty">No internal testers.</div> : (
          <div className="rounded-2xl border border-white/10">
            {rows.map((u) => (
              <div key={u.email} data-testid={`tester-row-${u.email}`} className="flex items-center justify-between border-b border-white/5 px-4 py-2.5 text-sm last:border-0">
                <div><span className="text-white/90">{u.email}</span> {!u.user_exists && <span className="ml-1 text-[10px] text-[#f59e0b]">no account yet</span>}<div className="text-[11px] text-[#A1A1AA]">added by {u.added_by} · {fmtDate(u.added_at)}</div></div>
                <button data-testid={`tester-remove-${u.email}`} onClick={() => remove(u.email)} className="rounded-lg border border-white/15 p-1.5 text-[#ef4444] hover:bg-white/5"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function CodebaseTab() {
  const [nodes, setNodes] = useState(null);   // flat list of visible nodes with depth/expanded
  const [file, setFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    api.adminCodebaseTree("").then((d) => {
      setNodes([...d.dirs, ...d.files].map((n) => ({ ...n, depth: 0, expanded: false })));
    }).catch(() => setNodes([]));
  }, []);

  const toggleDir = async (idx) => {
    const node = nodes[idx];
    if (node.type !== "dir") return;
    if (node.expanded) {
      // collapse: drop all deeper nodes that follow until depth <= node.depth
      const out = nodes.slice(0, idx + 1);
      out[idx] = { ...node, expanded: false };
      for (let i = idx + 1; i < nodes.length; i++) {
        if (nodes[i].depth <= node.depth) { out.push(...nodes.slice(i)); break; }
      }
      setNodes(out);
    } else {
      let children = node._children;
      if (!children) {
        try { const d = await api.adminCodebaseTree(node.path); children = [...d.dirs, ...d.files]; }
        catch { children = []; }
      }
      const kids = children.map((c) => ({ ...c, depth: node.depth + 1, expanded: false }));
      const out = [...nodes];
      out[idx] = { ...node, expanded: true, _children: children };
      out.splice(idx + 1, 0, ...kids);
      setNodes(out);
    }
  };

  const openFile = async (path) => {
    setLoadingFile(true); setFile({ path });
    try { setFile(await api.adminCodebaseFile(path)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot open file"); setFile(null); }
    finally { setLoadingFile(false); }
  };

  let viewer;
  if (!file) viewer = <div className="flex h-full items-center justify-center p-10 text-sm text-[#666]">Select a file to view its source (read-only).</div>;
  else if (loadingFile) viewer = <div className="flex items-center gap-2 p-4 text-sm text-[#A1A1AA]"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  else if (file.protected) viewer = <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center"><Lock size={28} className="text-amber-500" /><div className="text-sm font-semibold text-white">🔒 Protected file</div><div className="text-xs text-[#A1A1AA]">{file.message || "Contents are not viewable."}</div></div>;
  else viewer = (
    <>
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-xs"><span className="font-mono text-[#00F0FF]">{file.path}</span><span className="text-[#666]">{file.lines} lines · {(file.size / 1024).toFixed(1)} KB</span></div>
      <pre data-testid="code-file-content" className="max-h-[64vh] overflow-auto p-3 text-[11px] leading-relaxed text-[#d4d4d4]"><code>{file.content}</code></pre>
    </>
  );

  return (
    <div data-testid="codebase-tab" className="grid gap-3 lg:grid-cols-[300px_1fr]">
      <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-2">
        <div className="mb-1 flex items-center gap-1.5 px-1.5 text-[11px] font-bold uppercase tracking-wide text-[#666]"><FolderTree size={13} /> Caption Motion</div>
        {!nodes ? <div className="flex items-center gap-2 p-3 text-xs text-[#A1A1AA]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          : nodes.map((n, i) => n.type === "dir" ? (
            <button key={n.path} data-testid={`code-dir-${n.path}`} onClick={() => toggleDir(i)} style={{ paddingLeft: n.depth * 14 + 4 }}
              className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-sm text-[#A1A1AA] hover:bg-white/5 hover:text-white">
              {n.expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<Folder size={14} className="text-[#00F0FF]" /> {n.name}
            </button>
          ) : (
            <button key={n.path} data-testid={`code-file-${n.path}`} disabled={n.protected || !n.viewable}
              onClick={() => n.viewable && openFile(n.path)} style={{ paddingLeft: n.depth * 14 + 20 }}
              className={`flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-sm ${n.protected ? "text-[#666] cursor-not-allowed" : n.viewable ? "text-[#A1A1AA] hover:bg-white/5 hover:text-white" : "text-[#555] cursor-not-allowed"}`}>
              {n.protected ? <Lock size={13} className="text-amber-500/70" /> : <FileText size={13} />} {n.name}
              {n.protected && <span className="ml-auto text-[10px] text-amber-500/70">🔒 Protected</span>}
            </button>
          ))}
      </div>
      <div className="min-w-0 rounded-xl border border-white/10 bg-[#0b0b0b]">{viewer}</div>
    </div>
  );
}

const TABS = [["overview", "Overview", LayoutDashboard], ["codebase", "Codebase", FolderTree], ["releases", "Releases", Rocket], ["exports", "Export Versions", Zap], ["deployments", "Deployment History", GitCommit], ["changelog", "Change Log", FileText], ["rollout", "Feature Rollout", Sliders], ["testers", "Internal Testers", Users]];

function OverviewTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.adminCodebaseOverview().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex items-center gap-2 py-8 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading deployment metadata…</div>;
  const rows = [
    ["Application", d.application], ["Current Release", d.current_release || "—"],
    ["Release Status", (d.current_release_status || "—").toUpperCase()],
    ["Active Export Engine", `${d.export_engine_active} — ${d.export_engine_name}`],
    ["Environment", d.environment], ["Git SHA / Commit", d.git_sha || "—"],
    ["Frontend Version", d.frontend_version || "—"], ["Backend Version", d.backend_version || "—"],
    ["Process Started", d.process_started_at ? new Date(d.process_started_at).toLocaleString() : "—"],
    ["Releases Recorded", d.release_count],
  ];
  return (
    <div data-testid="codebase-overview" className="grid gap-2 sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-2.5">
          <span className="text-xs uppercase tracking-wide text-[#666]">{k}</span>
          <span className="ml-3 truncate text-sm font-semibold text-white" title={String(v)}>{v}</span>
        </div>
      ))}
      <p className="sm:col-span-2 mt-1 text-[11px] text-[#666]">Values are read live from git, the running process and the current release/engine state — not hard-coded.</p>
    </div>
  );
}

const fmtMs = (ms) => ms == null ? "—" : ms < 1000 ? `${ms} ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;

function DiagnosticsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.adminExportEngineDiagnostics().then(setD).catch(() => setD(null)); }, []);
  if (!d) return null;
  const Card = ({ label, val, hi }) => (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5"><div className="text-[10px] uppercase tracking-wide text-[#666]">{label}</div><div className={`mt-0.5 text-sm font-bold tabular-nums ${hi ? "text-[#00F0FF]" : "text-white"}`}>{val}</div></div>
  );
  return (
    <div data-testid="v2-diagnostics" className="space-y-3">
      <h4 className="text-sm font-bold text-white">Engine performance — V1 vs V2</h4>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-white/10 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]">V1 — full export (created→ready)</div>
          <div className="grid grid-cols-2 gap-2">
            <Card label="Exports" val={d.v1.exports} /><Card label="Completed" val={d.v1.completed} />
            <Card label="Avg total" val={fmtMs(d.v1.avg_ms)} /><Card label="P50 / P95" val={`${fmtMs(d.v1.p50_ms)} / ${fmtMs(d.v1.p95_ms)}`} />
            <Card label="Failure rate" val={`${Math.round((d.v1.failure_rate || 0) * 100)}%`} />
          </div>
        </div>
        <div className="rounded-xl border border-[#00F0FF]/20 p-3">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#00F0FF]">V2 — segment render + assembly</div>
          <div className="grid grid-cols-2 gap-2">
            <Card label="Final assemblies" val={d.v2.exports} /><Card label="Avg assembly" val={fmtMs(d.v2.avg_ms)} hi />
            <Card label="Segments rendered" val={d.v2.segments_rendered} /><Card label="Avg / segment" val={fmtMs(d.v2.avg_segment_ms)} />
            <Card label="Fastest seg" val={fmtMs(d.v2.fastest_segment_ms)} /><Card label="Slowest seg" val={fmtMs(d.v2.slowest_segment_ms)} />
          </div>
        </div>
      </div>
      <div className="text-[11px] text-[#666]">Hardware: {d.hardware.encoder}{d.hardware.gpu ? " · GPU" : " · no GPU"}. V2 moves render time into per-segment renders; Final Export is a fast stream-copy concat.</div>
      {(d.v2_manual || d.v2_automatic) && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="v2-mode-diagnostics">
          <div className="rounded-xl border border-white/10 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]">Manual mode</div>
            <div className="grid grid-cols-2 gap-2">
              <Card label="Final exports" val={d.v2_manual?.exports ?? 0} /><Card label="Segments" val={d.v2_manual?.segments_rendered ?? 0} />
              <Card label="Avg / segment" val={fmtMs(d.v2_manual?.avg_segment_ms)} /><Card label="Avg assembly" val={fmtMs(d.v2_manual?.avg_assembly_ms)} />
              <Card label="Avg validation" val={fmtMs(d.v2_manual?.avg_validation_ms)} /><Card label="Avg total" val={fmtMs(d.v2_manual?.avg_workflow_ms)} />
            </div>
          </div>
          <div className="rounded-xl border border-[#00F0FF]/20 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#00F0FF]">Automatic mode</div>
            <div className="grid grid-cols-2 gap-2">
              <Card label="Final exports" val={d.v2_automatic?.exports ?? 0} /><Card label="Segments" val={d.v2_automatic?.segments_rendered ?? 0} />
              <Card label="Avg / segment" val={fmtMs(d.v2_automatic?.avg_segment_ms)} /><Card label="Avg assembly" val={fmtMs(d.v2_automatic?.avg_assembly_ms)} hi />
              <Card label="Avg validation" val={fmtMs(d.v2_automatic?.avg_validation_ms)} /><Card label="Avg total" val={fmtMs(d.v2_automatic?.avg_workflow_ms)} />
            </div>
          </div>
        </div>
      )}
      {d.recent_segments.length > 0 && (
        <div className="rounded-xl border border-white/10">
          <div className="border-b border-white/10 px-3 py-2 text-xs font-bold text-[#A1A1AA]">Recent segment renders</div>
          <table className="w-full text-left text-xs"><tbody>
            {d.recent_segments.map((r, i) => (
              <tr key={i} className="border-t border-white/5"><td className="px-3 py-1.5 text-white">{r.project || "—"}</td><td className="px-3 py-1.5 text-[#A1A1AA]">{r.segment}</td><td className="px-3 py-1.5 truncate text-[#666]">{r.text}</td><td className="px-3 py-1.5 text-right font-bold tabular-nums text-[#00F0FF]">{fmtMs(r.ms)}</td></tr>
            ))}
          </tbody></table>
        </div>
      )}
    </div>
  );
}

function DeploymentHistoryTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.adminReleases().then((d) => setRows(d.releases || [])).catch(() => setRows([])); }, []);
  if (!rows) return <div className="flex items-center gap-2 py-8 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  const badge = { live: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", rolled_back: "text-red-300 border-red-500/40 bg-red-500/10", draft: "text-[#A1A1AA] border-white/15", ready: "text-amber-300 border-amber-500/40 bg-amber-500/10" };
  return (
    <div data-testid="deployment-history" className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-left text-sm">
        <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wide text-[#A1A1AA]"><tr><th className="px-3 py-2.5">Release</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Commit</th><th className="px-3 py-2.5">Deployed</th><th className="px-3 py-2.5">By</th></tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={5} className="px-3 py-6 text-center text-[#666]">No deployments recorded yet.</td></tr> :
            rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="px-3 py-2.5"><div className="font-bold text-white">{r.version}</div><div className="text-[11px] text-[#666]">{r.name}</div></td>
                <td className="px-3 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${badge[r.status] || "text-[#A1A1AA] border-white/15"}`}>{(r.status || "").replace("_", " ")}</span></td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-[#00F0FF]">{r.commit_sha || "—"}</td>
                <td className="px-3 py-2.5 text-[#A1A1AA]">{r.live_at ? new Date(r.live_at).toLocaleString() : new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-[11px] text-[#666]">{r.deployed_by || r.created_by || "—"}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function ChangeLogTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.adminReleases().then((d) => setRows(d.releases || [])).catch(() => setRows([])); }, []);
  if (!rows) return <div className="flex items-center gap-2 py-8 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  if (rows.length === 0) return <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-[#666]">No release notes yet.</div>;
  return (
    <div data-testid="change-log" className="space-y-3">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-white">{r.version}</span>
            {r.status === "live" && <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">LIVE</span>}
            <span className="ml-auto text-[11px] text-[#666]">{new Date(r.created_at).toLocaleDateString()} · {r.commit_sha || ""}</span>
          </div>
          {r.name && <div className="mt-0.5 text-xs text-[#A1A1AA]">{r.name}</div>}
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-[#d4d4d4]">{r.notes || "—"}</pre>
        </div>
      ))}
    </div>
  );
}

function V2ModeControl() {
  const [rows, setRows] = useState(null);
  const KEYS = [["export_v2_manual_rendering", "Manual Rendering", "Render each segment one at a time by hand."],
                ["export_v2_automatic_rendering", "Automatic Rendering", "Sequentially auto-render every segment, then auto-assemble."]];
  const load = useCallback(() => {
    import("@/lib/api").then(({ http }) => http.get("/admin/features").then((r) => {
      const map = {}; (r.data || []).forEach((f) => { map[f.feature_key] = f; }); setRows(map);
    }).catch(() => setRows({})));
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key, on) => {
    try { await api.adminFeatureUpdate(key, { access_mode: on ? "admin_only" : "off" }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!rows) return null;
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="v2-mode-control">
      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]">V2 Rendering Modes</div>
      <div className="space-y-2">
        {KEYS.map(([key, name, desc]) => {
          const f = rows[key] || {}; const on = f.access_mode !== "off";
          return (
            <div key={key} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2" data-testid={`v2-mode-row-${key}`}>
              <div><div className="text-sm font-semibold text-white">{name} <span className="ml-1 text-[10px] text-[#666]">({f.access_mode ? ACCESS_LABEL[f.access_mode] : "—"} · {f.plan || "free"})</span></div><div className="text-[11px] text-[#A1A1AA]">{desc}</div></div>
              <button data-testid={`v2-mode-toggle-${key}`} onClick={() => toggle(key, !on)}
                className={`min-w-[70px] rounded-lg px-3 py-1.5 text-xs font-bold ${on ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{on ? "ON" : "OFF"}</button>
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 text-[10px] text-[#666]">Fine-grained access/plan for each mode is managed in the Feature Rollout tab.</div>
    </div>
  );
}

function ExportVersionsTab() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => { api.adminExportEngines().then(setD).catch(() => setD({ engines: [], active: "V1" })); }, []);
  useEffect(() => { load(); }, [load]);
  const doSwitch = async (version) => {
    setBusy(true);
    try { const r = await api.adminExportEngineSwitch(version, confirm?.reason || ""); toast.success(`Active export engine → ${r.active}`); setConfirm(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Switch failed"); }
    finally { setBusy(false); }
  };
  if (!d) return <div className="flex items-center gap-2 py-8 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Loading export engines…</div>;
  const statusCls = { stable: "text-emerald-300 border-emerald-500/30", testing: "text-amber-300 border-amber-500/30", development: "text-sky-300 border-sky-500/30", disabled: "text-red-300 border-red-500/30" };
  return (
    <div data-testid="export-versions" className="space-y-3">
      <div className="flex items-center gap-2 rounded-lg border border-[#00F0FF]/25 bg-[#00F0FF]/5 px-3.5 py-2.5 text-sm">
        <Zap size={15} className="text-[#00F0FF]" />
        <span className="text-[#A1A1AA]">Active production export engine:</span>
        <span data-testid="active-engine" className="font-bold text-white">{d.active}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wide text-[#A1A1AA]">
            <tr><th className="px-3 py-2.5">Version</th><th className="px-3 py-2.5">Released</th><th className="px-3 py-2.5">Name</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Exports</th><th className="px-3 py-2.5">Active</th><th className="px-3 py-2.5"></th></tr>
          </thead>
          <tbody>
            {d.engines.map((e) => (
              <tr key={e.version} data-testid={`engine-row-${e.version}`} className="border-t border-white/5">
                <td className="px-3 py-2.5 font-bold text-white">{e.version}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums text-[#A1A1AA]" data-testid={`engine-date-${e.version}`}>{e.created || "—"}</td>
                <td className="px-3 py-2.5"><div className="text-white">{e.name}</div><div className="max-w-md text-[11px] text-[#666]">{e.description}</div></td>
                <td className="px-3 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusCls[e.status] || "text-[#A1A1AA] border-white/15"}`}>{e.status}</span></td>
                <td className="px-3 py-2.5 tabular-nums text-[#A1A1AA]">{e.exports_count}</td>
                <td className="px-3 py-2.5">{e.active ? <span className="inline-flex items-center gap-1 text-emerald-300"><Check size={14} /> Active</span> : <span className="text-[#666]">—</span>}</td>
                <td className="px-3 py-2.5">{!e.active && (
                  <button data-testid={`activate-${e.version}`} disabled={busy} onClick={() => setConfirm({ version: e.version, name: e.name, from: d.active, reason: "" })}
                    className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/5 disabled:opacity-50">Make Active</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#666]">Switching affects NEW exports only. Running and completed jobs keep the engine they were stamped with. V1 is never removed.</p>
      <V2ModeControl />
      <div className="mt-4 border-t border-white/10 pt-4"><DiagnosticsPanel /></div>
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="engine-switch-dialog" className="border-white/10 bg-[#0b0b0b] text-white">
          <DialogTitle>Switch production export engine?</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Change the active engine from <b className="text-white">{confirm?.from}</b> to <b className="text-white">{confirm?.version} — {confirm?.name}</b>. New exports will use it immediately.</DialogDescription>
          <input value={confirm?.reason || ""} onChange={(e) => setConfirm((c) => ({ ...c, reason: e.target.value }))} placeholder="Reason (optional, for the audit log)"
            className="mt-2 w-full rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-[#00F0FF]/50 focus:outline-none" />
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white">Cancel</button>
            <button data-testid="confirm-engine-switch" disabled={busy} onClick={() => doSwitch(confirm.version)} className="rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-50">{busy ? "Switching…" : "Confirm Switch"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Releases() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-5" data-testid="releases-section">
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(([v, l, Icon]) => (
          <button key={v} data-testid={`rel-subtab-${v}`} onClick={() => setTab(v)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === v ? "bg-[#00F0FF] text-black" : "border border-white/15 text-[#A1A1AA] hover:text-white"}`}><Icon size={15} /> {l}</button>
        ))}
      </div>
      {tab === "overview" && <OverviewTab />}
      {tab === "codebase" && <CodebaseTab />}
      {tab === "releases" && <ReleasesTab />}
      {tab === "exports" && <ExportVersionsTab />}
      {tab === "deployments" && <DeploymentHistoryTab />}
      {tab === "changelog" && <ChangeLogTab />}
      {tab === "rollout" && <RolloutTab />}
      {tab === "testers" && <TestersTab />}
    </div>
  );
}
